// src/App.js
import React, { useState, useEffect } from "react";
import "./styles/Login.css";
import AuraKartLogo from "./assets/Aurakart_logo.jpg";
import PartnerPortal from "./components/PartnerPortal";
import SellerLogin from "./components/SellerLogin";
import config from "./config";

export default function App() {
  // Admin login state
  const [username, setUsername] = useState(""); // email or phone
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'admin'
  const [loginType, setLoginType] = useState("admin"); // 'admin' | 'seller'
  const [error, setError] = useState("");
  const [sellerId, setSellerId] = useState(null); // used only for admin->seller view if needed

  // ---------- LOAD SESSION FROM localStorage (ADMIN ONLY) ----------
  useEffect(() => {
    const storedIsLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const storedRole = localStorage.getItem("userRole");
    const storedUsername = localStorage.getItem("username");

    if (storedIsLoggedIn && storedRole === "admin") {
      setIsLoggedIn(true);
      setUserRole("admin");
      if (storedUsername) setUsername(storedUsername);
    }
  }, []);

  // ---------- ADMIN LOGIN ----------
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (loginType === "admin") {
      try {
        // Call backend auth
        const res = await fetch(`${config.baseUrl}/api/auth/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            login: username, // email or phone
            password: password,
          }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || "Invalid credentials");
        }

        const data = await res.json();

        // Make sure this user is an ADMIN
        if (!data.role || data.role.toUpperCase() !== "ADMIN") {
          throw new Error("You are not allowed to access the admin portal.");
        }

        // You could also save data.id etc. if needed later
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userRole", "admin");
        localStorage.setItem("username", data.email || username);
        localStorage.setItem("admin_user_id", data.id);

        setIsLoggedIn(true);
        setUserRole("admin");
        setUsername(data.email || username);
      } catch (err) {
        console.error("Admin login failed:", err);
        setError(err.message || "Login failed. Please try again.");
      }

      return;
    }

    // (Seller login is handled in SellerLogin component)
  };

  // ---------- LOGOUT (ADMIN) ----------
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole(null);
    setUsername("");
    setPassword("");
    setLoginType("admin");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userRole");
    localStorage.removeItem("username");
    localStorage.removeItem("sellerId");
    localStorage.removeItem("admin_user_id");
  };

  // ---------- WHEN SELLER TAB SELECTED & NOT LOGGED IN ----------
  if (!isLoggedIn && loginType === "seller") {
    return <SellerLogin onBackToAdmin={() => setLoginType("admin")} />;
  }

  // ---------- LOGIN PAGE (ADMIN) ----------
  if (!isLoggedIn) {
    return (
      <div className="login-container">
        <img src={AuraKartLogo} alt="AuraKart Logo" className="login-logo" />
        <h1>AuraKart Partner Portal</h1>

        {/* Login Type Toggle */}
        <div className="login-type-toggle">
          <button
            className={`toggle-btn ${loginType === "admin" ? "active" : ""}`}
            onClick={() => {
              setLoginType("admin");
              setError("");
              setUsername("");
              setPassword("");
            }}
          >
            👨‍💼 Admin
          </button>
          <button
            className={`toggle-btn ${loginType === "seller" ? "active" : ""}`}
            onClick={() => {
              setLoginType("seller");
              setError("");
            }}
          >
            🏪 Seller
          </button>
        </div>

        {/* Error */}
        {error && <div className="error-message">{error}</div>}

        {/* Admin Login Form */}
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="text"
            placeholder="Admin email or phone"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">Login</button>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD (ADMIN) ----------
  return (
    <PartnerPortal
      onLogout={handleLogout}
      userRole={userRole}
      sellerId={sellerId}
    />
  );
}
